import { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, GeoJSON } from 'react-leaflet';
import L from 'leaflet';
import { LoadingState, EmptyState } from '../shared/LoadingStates';
import { loadStateDistricts, getUserState, getAvailableStates, createFeatureCollection } from '../../lib/geojsonLoader';
import 'leaflet/dist/leaflet.css';

// Inject CSS for pulsing animations
const pulseStyles = `
  @keyframes pulse-blue {
    0%, 100% { fill-opacity: 0.5; }
    50% { fill-opacity: 0.8; }
  }
  @keyframes pulse-red {
    0%, 100% { fill-opacity: 0.5; }
    50% { fill-opacity: 0.8; }
  }
  @keyframes pulse-purple {
    0%, 100% { fill-opacity: 0.6; stroke-width: 3px; }
    50% { fill-opacity: 0.9; stroke-width: 4px; }
  }
  .district-flippable-dem-path {
    animation: pulse-blue 2s ease-in-out infinite;
    stroke: #1d4ed8 !important;
    stroke-width: 3px !important;
  }
  .district-flippable-rep-path {
    animation: pulse-red 2s ease-in-out infinite;
    stroke: #b91c1c !important;
    stroke-width: 3px !important;
  }
  .district-tossup-path {
    animation: pulse-purple 1.5s ease-in-out infinite;
    stroke: #7c3aed !important;
    stroke-width: 4px !important;
  }
`;

// Add styles to document head
if (typeof document !== 'undefined') {
  const styleEl = document.createElement('style');
  styleEl.textContent = pulseStyles;
  if (!document.head.querySelector('#map-pulse-styles')) {
    styleEl.id = 'map-pulse-styles';
    document.head.appendChild(styleEl);
  }
}

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Interactive map of all 435 congressional districts
export default function MapTab() {
  const [selectedState, setSelectedState] = useState(null);
  const [districtFeatures, setDistrictFeatures] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const availableStates = getAvailableStates();

  // Load user's state on mount
  useEffect(() => {
    async function initializeMap() {
      try {
        setIsLoading(true);
        const userState = await getUserState();
        setSelectedState(userState);
        await loadDistrictsForState(userState);
      } catch (err) {
        console.error('Error initializing map:', err);
        setError(err.message);
        // Fallback to CA
        setSelectedState('CA');
        await loadDistrictsForState('CA');
      } finally {
        setIsLoading(false);
      }
    }

    initializeMap();
  }, []);

  // Load districts when state changes
  const loadDistrictsForState = async (stateCode) => {
    try {
      console.log(`🔄 Starting to load districts for ${stateCode}...`);
      setIsLoading(true);
      setError(null);

      const features = await loadStateDistricts(stateCode);
      console.log(`📦 Received ${features?.length || 0} features from loadStateDistricts`);

      if (!features || features.length === 0) {
        throw new Error(`No features returned for ${stateCode}`);
      }

      const featureCollection = createFeatureCollection(features);
      console.log(`📦 Created FeatureCollection:`, {
        type: featureCollection.type,
        featureCount: featureCollection.features.length,
        firstFeature: featureCollection.features[0]?.properties
      });

      setDistrictFeatures(featureCollection);
      console.log(`✅ Map loaded: ${featureCollection.features.length} districts for ${stateCode}`);

      // DEBUG: Log featureCollection details
      console.log('FeatureCollection structure:', {
        type: featureCollection.type,
        featureCount: featureCollection.features.length,
        firstFeatureType: featureCollection.features[0]?.type,
        firstGeometryType: featureCollection.features[0]?.geometry?.type,
        hasCoordinates: !!featureCollection.features[0]?.geometry?.coordinates,
        coordinateCount: featureCollection.features[0]?.geometry?.coordinates?.length
      });
    } catch (err) {
      console.error(`❌ Error loading ${stateCode} districts:`, err);
      console.error('Error stack:', err.stack);
      setError(`Failed to load ${stateCode} districts: ${err.message}`);
      setDistrictFeatures(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStateChange = async (e) => {
    const newState = e.target.value;
    setSelectedState(newState);
    await loadDistrictsForState(newState);
  };

  // Determine if a district is flippable based on rating
  const isFlippable = (rating) => {
    return ['Lean D', 'Lean R', 'Toss-Up', 'Likely D', 'Likely R'].includes(rating);
  };

  // Style for district boundaries - color-coded by current representative
  const districtStyle = (feature) => {
    const party = feature.properties?.incumbent_party;
    const rating = feature.properties?.model_rating;
    const margin = feature.properties?.margin_2024;

    // Determine color based on incumbent party (who currently holds the seat)
    let fillColor = '#94a3b8'; // Default gray for unknown/no data
    let borderColor = '#64748b'; // Gray border
    let fillOpacity = 0.5;
    let weight = 2;

    if (party === 'Democratic') {
      // Blue spectrum based on safety
      if (rating === 'Safe D') {
        fillColor = '#1e40af'; // Deep blue
        borderColor = '#1e3a8a';
        fillOpacity = 0.7;
      } else if (rating === 'Likely D') {
        fillColor = '#2563eb'; // Medium blue
        borderColor = '#1d4ed8';
        fillOpacity = 0.6;
      } else {
        fillColor = '#3b82f6'; // Light blue (competitive)
        borderColor = '#2563eb';
        fillOpacity = 0.5;
        weight = 3; // Thicker border for competitive
      }
    } else if (party === 'Republican') {
      // Red spectrum based on safety
      if (rating === 'Safe R') {
        fillColor = '#991b1b'; // Deep red
        borderColor = '#7f1d1d';
        fillOpacity = 0.7;
      } else if (rating === 'Likely R') {
        fillColor = '#dc2626'; // Medium red
        borderColor = '#b91c1c';
        fillOpacity = 0.6;
      } else {
        fillColor = '#ef4444'; // Light red (competitive)
        borderColor = '#dc2626';
        fillOpacity = 0.5;
        weight = 3; // Thicker border for competitive
      }
    }

    // Toss-ups get purple regardless of incumbent
    if (rating === 'Toss-Up') {
      fillColor = '#7c3aed'; // Vivid purple
      borderColor = '#6d28d9';
      fillOpacity = 0.6;
      weight = 4;
    }

    // Lean ratings get special treatment (these are the flippable ones)
    if (rating === 'Lean D' || rating === 'Lean R') {
      weight = 3;
      fillOpacity = 0.55;
    }

    return {
      color: borderColor,
      weight: weight,
      opacity: 1,
      fillColor: fillColor,
      fillOpacity: fillOpacity
    };
  };

  const onEachFeature = (feature, layer) => {
    if (feature.properties && feature.properties.district_code) {
      const props = feature.properties;
      const party = props.incumbent_party || 'Unknown';
      const rating = props.model_rating || 'Not rated';
      const partyColor = party === 'Democratic' ? '#3b82f6' : party === 'Republican' ? '#ef4444' : '#64748b';

      // Add CSS class for flippable districts to enable pulsing animation
      if (rating === 'Toss-Up') {
        layer.getElement?.()?.classList.add('district-tossup');
        // For SVG layers, we need to wrap in a group with class
        if (layer._path) {
          layer._path.classList.add('district-tossup-path');
        }
      } else if (rating === 'Lean D' || rating === 'Likely D') {
        if (layer._path) {
          layer._path.classList.add('district-flippable-dem-path');
        }
      } else if (rating === 'Lean R' || rating === 'Likely R') {
        if (layer._path) {
          layer._path.classList.add('district-flippable-rep-path');
        }
      }

      // Determine flip potential indicator
      let flipIndicator = '';
      if (rating === 'Toss-Up') {
        flipIndicator = '<div style="margin: 4px 0; padding: 4px 8px; background: linear-gradient(90deg, #3b82f6, #7c3aed, #ef4444); color: white; font-weight: bold; border-radius: 4px; text-align: center;">⚔️ TOSS-UP - FLIPPABLE</div>';
      } else if (rating === 'Lean D') {
        flipIndicator = '<div style="margin: 4px 0; padding: 4px 8px; background: #3b82f6; color: white; font-weight: bold; border-radius: 4px; text-align: center;">📊 Lean D - Competitive</div>';
      } else if (rating === 'Lean R') {
        flipIndicator = '<div style="margin: 4px 0; padding: 4px 8px; background: #ef4444; color: white; font-weight: bold; border-radius: 4px; text-align: center;">📊 Lean R - Competitive</div>';
      } else if (rating === 'Likely D') {
        flipIndicator = '<div style="margin: 4px 0; padding: 4px 8px; background: #2563eb; color: white; border-radius: 4px; text-align: center;">Likely D</div>';
      } else if (rating === 'Likely R') {
        flipIndicator = '<div style="margin: 4px 0; padding: 4px 8px; background: #dc2626; color: white; border-radius: 4px; text-align: center;">Likely R</div>';
      }

      layer.bindPopup(`
        <div class="font-sans" style="min-width: 160px; max-width: 260px;">
          <div class="font-bold" style="color: ${partyColor}; font-size: 16px;">${props.district_code}</div>
          <div style="font-size: 12px; color: #64748b; margin-bottom: 6px;">${props.state_name}</div>
          ${flipIndicator}
          <hr style="border-color: #e5e7eb; margin: 6px 0;">
          <div style="font-size: 12px;">
            <div style="margin: 3px 0;">
              <strong>Rep:</strong>
              <span style="color: ${partyColor}; font-weight: bold;">${party}</span>
            </div>
            <div style="margin: 3px 0;">
              <strong>Rating:</strong> ${rating}
            </div>
            ${props.margin_2024 ? `
              <div style="margin: 3px 0;">
                <strong>Margin:</strong>
                <span style="color: ${props.margin_2024 > 0 ? '#3b82f6' : '#ef4444'}; font-weight: bold;">
                  ${props.margin_2024 > 0 ? 'D+' : 'R+'}${Math.abs(props.margin_2024).toFixed(1)}%
                </span>
              </div>
            ` : ''}
            ${props.is_open_seat ? `
              <div style="margin: 4px 0; padding: 3px 6px; background: #f59e0b; color: white; font-weight: bold; border-radius: 4px; text-align: center; font-size: 11px;">
                🆕 OPEN SEAT
              </div>
            ` : ''}
          </div>
        </div>
      `, { maxWidth: 280, minWidth: 140 });

      // Hover effect
      layer.on('mouseover', function() {
        this.setStyle({
          weight: 5,
          fillOpacity: 0.8
        });
        this.bringToFront();
      });

      layer.on('mouseout', function() {
        // Reset to original style
        const originalStyle = districtStyle(feature);
        this.setStyle(originalStyle);
      });
    }
  };

  // Calculate map bounds based on state
  const getMapCenter = () => {
    const centers = {
      'CA': [36.7783, -119.4179],
      'NY': [43.2994, -74.2179],
      'TX': [31.9686, -99.9018],
      'FL': [27.6648, -81.5158],
      'PA': [41.2033, -77.1945],
      // Add more as needed, or calculate from GeoJSON bounds
    };
    return centers[selectedState] || [39.8283, -98.5795]; // US center as fallback
  };

  const getMapZoom = () => {
    // Smaller states need higher zoom
    const singleDistrictStates = ['AK', 'DE', 'ND', 'SD', 'VT', 'WY'];
    if (singleDistrictStates.includes(selectedState)) {
      return 6;
    }
    return selectedState === 'TX' || selectedState === 'CA' ? 5 : 6;
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <LoadingState />
        <p className="text-white ml-4">Loading {selectedState ? `${selectedState} districts` : 'map'}...</p>
      </div>
    );
  }

  if (error) {
    return (
      <EmptyState
        icon="⚠️"
        title="Map Error"
        description={error}
        action={
          <button
            onClick={() => loadDistrictsForState(selectedState)}
            className="mt-4 px-4 py-2 bg-accent-blue-dark text-white rounded-lg font-semibold hover:bg-accent-blue-dark/90"
          >
            Retry
          </button>
        }
      />
    );
  }

  return (
    <div className="h-full flex flex-col" id="map-panel" role="tabpanel">
      {/* State Selector */}
      <div className="bg-slate-800 border-b border-slate-700 p-3 sm:p-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
          <label htmlFor="state-select" className="text-white font-semibold text-sm sm:text-base whitespace-nowrap">
            Select State:
          </label>
          <select
            id="state-select"
            value={selectedState || ''}
            onChange={handleStateChange}
            className="flex-1 w-full sm:max-w-md px-3 py-2.5 sm:px-4 sm:py-2 bg-slate-700 text-white rounded-lg border border-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base min-h-[44px]"
          >
            {availableStates.map(state => (
              <option key={state.code} value={state.code}>
                {state.name} ({state.districts})
              </option>
            ))}
          </select>

          {districtFeatures && (
            <div className="text-slate-400 text-xs sm:text-sm">
              {districtFeatures.features.length} districts
            </div>
          )}
        </div>
      </div>

      {/* Map Container */}
      <div className="flex-1 relative" style={{ minHeight: '500px' }}>
        {districtFeatures && districtFeatures.features.length > 0 ? (
          <>
            <MapContainer
              key={selectedState} // Force re-render when state changes
              center={getMapCenter()}
              zoom={getMapZoom()}
              style={{ height: '100%', width: '100%', minHeight: '500px', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
              className="z-0"
              scrollWheelZoom={true}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <GeoJSON
                key={selectedState} // Force re-render when state changes
                data={districtFeatures}
                style={districtStyle}
                onEachFeature={onEachFeature}
              />
            </MapContainer>

            {/* Legend - Responsive: compact on mobile, full on desktop */}
            <div className="absolute bottom-20 left-2 sm:top-4 sm:bottom-auto sm:right-4 sm:left-auto bg-white/95 backdrop-blur-sm rounded-lg shadow-xl p-2 sm:p-3 z-[1000] border border-slate-300 max-w-[140px] sm:max-w-none">
              <div className="font-bold text-[10px] sm:text-xs mb-1.5 sm:mb-2 text-slate-800">Legend</div>
              <div className="grid grid-cols-2 sm:grid-cols-1 gap-1 sm:gap-1.5 text-[10px] sm:text-xs">
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded border border-blue-800 flex-shrink-0" style={{ backgroundColor: '#1e40af' }}></div>
                  <span className="text-slate-700 truncate">Safe D</span>
                </div>
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded border border-red-800 flex-shrink-0" style={{ backgroundColor: '#991b1b' }}></div>
                  <span className="text-slate-700 truncate">Safe R</span>
                </div>
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded border border-blue-600 flex-shrink-0" style={{ backgroundColor: '#3b82f6' }}></div>
                  <span className="text-slate-700 truncate">Lean D</span>
                </div>
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded border border-red-600 flex-shrink-0" style={{ backgroundColor: '#ef4444' }}></div>
                  <span className="text-slate-700 truncate">Lean R</span>
                </div>
                <div className="flex items-center gap-1.5 sm:gap-2 col-span-2 sm:col-span-1">
                  <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded border border-purple-600 flex-shrink-0" style={{ backgroundColor: '#7c3aed' }}></div>
                  <span className="text-slate-700 font-semibold truncate">Toss-Up</span>
                </div>
              </div>
              <div className="hidden sm:block mt-2 pt-2 border-t border-slate-200">
                <div className="text-[10px] text-slate-500 italic">Pulsing = Flippable</div>
              </div>
            </div>
          </>
        ) : (
          <div className="h-full flex items-center justify-center bg-slate-900">
            <EmptyState
              icon="🗺️"
              title="No Districts Found"
              description={`No boundary data available for ${selectedState}`}
            />
          </div>
        )}
      </div>
    </div>
  );
}
